import React from 'react';
import ApexChart from 'react-apexcharts';
import moment from 'moment';
import { useFetch, RemoteSession } from '@mss/mss-component-library';
import { useTranslation } from 'react-i18next';

const dateFormat = 'YYYY-MM-DD';

const defaultDays: Record<string, number> = Array.from({ length: 7 }, (_, i) =>
  moment().subtract(i, 'days').format(dateFormat)
).reduce((acc, date) => {
  return { [date]: 0, ...acc };
}, {});

type Props = {
  colors: string[];
};

const RemoteSessionChart = ({ colors }: Props) => {
  const { t } = useTranslation();
  const [{ data: remoteSessionsCount, loading: remoteSessionsLoading }] =
    useFetch<RemoteSession[], Record<string, number>>({
      url: '/api/remote-session/',
      dataParser: data => {
        if (data.length === 0) {
          return { ...defaultDays };
        }
        return data.reduce(
          (acc, obj) => {
            const date = moment(obj.last_activity).format(dateFormat);

            if (Object.keys(defaultDays).includes(date)) {
              acc[date] = acc[date] + 1;
            }

            return acc;
          },
          { ...defaultDays }
        );
      },
    });

  return (
    <ApexChart
      type="bar"
      series={[
        {
          name: 'Sessions',
          data: remoteSessionsLoading
            ? [0, 1, 2, 3, 4, 5, 6]
            : Object.values(remoteSessionsCount ?? {}),
        },
      ]}
      width={400}
      options={{
        legend: { width: 150 },
        colors: remoteSessionsLoading ? ['#f8f8f8'] : colors,
        chart: {
          height: 250,
          type: 'bar',
          toolbar: {
            show: false,
          },
        },
        dataLabels: {
          enabled: true,
          offsetY: -20,
        },
        fill: { opacity: 1 },
        xaxis: {
          labels: { style: { fontFamily: 'inherit', fontSize: '11px' } },
          categories: Array.from({ length: 7 }, (_, i) =>
            moment().subtract(i, 'days').format('YYYY-MM-DD')
          ).reverse(),
        },
        yaxis: {
          labels: {
            formatter: (val: number) => val?.toFixed(0),
            style: { fontFamily: 'inherit', fontSize: '12px' },
          },
        },
        title: {
          text: t('Remote sessions in the last 7 days'),
          floating: true,
          offsetY: 228,
          align: 'center',
          style: {
            fontFamily: 'inherit',
            fontWeight: 600,
            fontSize: '14px',
          },
        },
        plotOptions: {
          bar: {
            horizontal: false,
            dataLabels: {
              position: 'top',
            },
          },
        },
      }}
    />
  );
};

export default RemoteSessionChart;
